/* ============================================================
   teg — game servers.

   Minecraft, Terraria and Factorio, each with the one control
   that matters and the state to decide whether to use it. The
   dashboard never holds the game password: every action goes to
   this box's own API, which forwards it.

   Minecraft is the odd one out — there is no "start" as such,
   only "run this modpack", so starting and switching are the
   same button with a pack beside it.
   ============================================================ */

import React, { useCallback, useEffect, useState } from 'react';
import { Icon, fmtBytes, fmtUptime, fmtClock } from './lib';
import { api } from './api';

function Confirm({ title, body, confirmLabel, onConfirm, onClose, danger }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="modal-backdrop tg-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal tg-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head"><span className="label">{title}</span>
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Close"><Icon name="close" size={14}/></button>
        </div>
        <div className="modal-body"><div className="tg-confirm">{body}</div></div>
        <div className="modal-foot">
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
          <button type="button" className={`btn btn--sm ${danger ? 'btn--danger' : ''}`} disabled={busy}
                  onClick={async () => { setBusy(true); try { await onConfirm(); onClose(); } finally { setBusy(false); } }}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Every card is the same object: a header, a fixed grid of the same four
   facts, an optional extra block, and one control row in one order. Cards
   that each invent their own shape are harder to compare than the numbers
   inside them. */
function GameCard({ game, facts, extra, controls }) {
  return (
    <div className="panel tg-game">
      <div className="panel-head tg-game-head">
        <span className="tg-game-name">
          <Icon name="game" size={16}/>
          {game.name}
        </span>
        <span className="tg-game-state">
          <span className={`dot ${game.running ? 'dot--ok' : 'dot--err'}`}/>
          <span className="meta">{game.error ? 'unreachable' : game.state}</span>
        </span>
      </div>
      <div className="tg-game-body">
        <dl className="tg-facts tg-facts--pairs">
          {facts.map(([k, v]) => (
            <div key={k}><dt>{k}</dt><dd title={typeof v === 'string' ? v : undefined}>{v ?? '—'}</dd></div>
          ))}
        </dl>
        {game.error && <div className="alert alert--warn tg-banner">{game.error}</div>}
        <div className="tg-game-extra">{extra}</div>
      </div>
      <div className="tg-game-foot">{controls}</div>
    </div>
  );
}

export default function Games({ token, games, toast, onGamesChanged }) {
  const [packs, setPacks] = useState([]);
  const [pack, setPack] = useState('');
  const [mcStats, setMcStats] = useState(null);
  const [terraria, setTerraria] = useState(null);
  const [busy, setBusy] = useState('');
  const [dialog, setDialog] = useState(null);

  const mc = (games || []).find((g) => g.id === 'minecraft');
  const tr = (games || []).find((g) => g.id === 'terraria');
  const fa = (games || []).find((g) => g.id === 'factorio');

  useEffect(() => {
    let live = true;
    api.mcPacks(token).then((d) => { if (live) setPacks(d.servers || []); }).catch(() => {});
    api.mcStats(token).then((d) => { if (live) setMcStats(d); }).catch(() => {});
    api.terraria(token).then((d) => { if (live) setTerraria(d); }).catch(() => {});
    return () => { live = false; };
  }, [token]);

  useEffect(() => { if (mc?.pack_id && !pack) setPack(mc.pack_id); }, [mc, pack]);

  const control = useCallback(async (game, action, extra, label) => {
    setBusy(`${game}:${action}`);
    try {
      await api.control(token, game, action, extra);
      toast('ok', label);
      setTimeout(onGamesChanged, 2500);
    } catch (e) {
      toast('err', `${label} failed: ${e.message}`);
    } finally { setBusy(''); }
  }, [token, toast, onGamesChanged]);

  const controlRow = (id, g) => (
    <>
      <button type="button" className="btn btn--sm" disabled={!!busy || g.running}
              onClick={() => control(id, 'start', {}, `${g.name} starting`)}>
        <Icon name="play" size={13}/> Start
      </button>
      <button type="button" className="btn btn--ghost btn--sm" disabled={!!busy || !g.running}
              onClick={() => setDialog({
                title: `Stop ${g.name}`,
                body: <p>Anyone playing will be disconnected. {id === 'factorio' ? 'Factorio saves on shutdown.' : 'The world is saved first.'}</p>,
                confirmLabel: 'Stop it', danger: true,
                onConfirm: () => control(id, 'stop', {}, `${g.name} stopping`),
              })}>
        <Icon name="stop" size={13}/> Stop
      </button>
      <button type="button" className="btn btn--ghost btn--sm" disabled={!!busy}
              onClick={() => setDialog({
                title: `Restart ${g.name}`,
                body: <p>Players are disconnected and reconnect once it is back.</p>,
                confirmLabel: 'Restart',
                onConfirm: () => control(id, 'restart', {}, `${g.name} restarting`),
              })}>
        <Icon name="restart" size={13}/> Restart
      </button>
    </>
  );

  return (
    <div className="tg-view">
      <div className="tg-view-head">
        <h2 className="h2">Game servers</h2>
        <span className="meta">{(games || []).filter((g) => g.running).length} of {(games || []).length} running</span>
      </div>

      {!games && <div className="skeleton tg-skel"/>}

      <div className="tg-grid tg-grid--games">
        {mc && (
          <GameCard
            game={mc}
            facts={[
              ['Address', 'mc.ojee.net'],
              ['Port', '25565'],
              ['Running', mc.detail ? `${mc.detail}${mc.version ? ` ${mc.version}` : ''}` : '—'],
              ['Players known', mcStats?.totals ? String(mcStats.totals.players) : '—'],
              ['Hours played', mcStats?.totals ? String(Math.round(mcStats.totals.hours)) : '—'],
            ]}
            controls={
            <div className="tg-game-controls tg-game-controls--mc">
              <select className="select tg-packpick" value={pack} onChange={(e) => setPack(e.target.value)}
                      aria-label="Modpack">
                {packs.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.version}</option>)}
                {packs.length === 0 && <option value="">loading packs…</option>}
              </select>
              <button type="button" className="btn btn--sm" disabled={!!busy || !pack}
                      onClick={() => {
                        const target = packs.find((p) => p.id === pack);
                        const mode = !mc.running ? 'start' : pack === mc.pack_id ? 'restart' : 'switch';
                        setDialog({
                          title: { start: `Start ${target?.name}`, restart: `Restart ${target?.name}`,
                                   switch: `Switch to ${target?.name}` }[mode],
                          body: mode === 'switch'
                            ? <p>{mc.detail} stops first, then {target?.name} starts. Anyone playing is disconnected, and the switch takes a minute or two.</p>
                            : mode === 'restart'
                              ? <p>{target?.name} stops and starts again. Anyone playing is disconnected for a minute or two.</p>
                              : <p>{target?.name} {target?.version} starts on mc.ojee.net. It takes a minute or two to accept players.</p>,
                          confirmLabel: { start: 'Start it', restart: 'Restart it', switch: 'Switch pack' }[mode],
                          onConfirm: () => control('minecraft', 'switch', { pack },
                            { start: 'Starting Minecraft', restart: 'Restarting Minecraft', switch: 'Switching modpack' }[mode]),
                        });
                      }}>
                <Icon name={mc.running ? 'restart' : 'play'} size={13}/>
                {!mc.running ? 'Start' : pack === mc.pack_id ? 'Restart' : 'Switch'}
              </button>
              <button type="button" className="btn btn--ghost btn--sm" disabled={!!busy || !mc.running}
                      onClick={() => setDialog({
                        title: 'Stop Minecraft',
                        body: <p>The world saves, then the server stops. Anyone playing is disconnected.</p>,
                        confirmLabel: 'Stop it', danger: true,
                        onConfirm: () => control('minecraft', 'stop', {}, 'Minecraft stopping'),
                      })}>
                <Icon name="stop" size={13}/> Stop
              </button>
            </div>
          }/>
        )}

        {tr && (
          <GameCard
            game={tr}
            facts={[
              ['Address', tr.host],
              ['Port', tr.port ? String(tr.port) : '—'],
              ['Running', tr.detail],
              ['Uptime', tr.uptime ? fmtUptime(tr.uptime) : '—'],
              ['Memory', tr.memory_mb ? fmtBytes(tr.memory_mb * 1024 * 1024) : '—'],
              ['Players', terraria ? `${terraria.players_online ?? 0} of ${terraria.max_players ?? '?'}` : '—'],
              ['World', terraria?.world?.name],
              ['Bosses', terraria ? `${terraria.bosses_defeated ?? 0} of ${terraria.bosses_total ?? 0}` : '—'],
              ['In-game time', terraria?.game_time],
            ]}
            extra={terraria?.players?.length > 0 && (
              <div className="tg-mini-list">
                <span className="label">Online now</span>
                {terraria.players.map((pl) => (
                  <div key={pl.name} className="listrow tg-mini-row">
                    <span className="tg-mini-name">{pl.name}</span>
                    <span className="meta">since {fmtClock(pl.since)}</span>
                  </div>
                ))}
              </div>
            )}
            controls={<div className="tg-game-controls">{controlRow('terraria', tr)}</div>}/>
        )}

        {fa && (
          <GameCard
            game={fa}
            facts={[
              ['Address', fa.host],
              // Factorio speaks UDP; a TCP probe would always call it closed.
              ['Port', fa.port ? `${fa.port} · udp` : '—'],
              ['Running', fa.detail],
              ['Uptime', fa.uptime ? fmtUptime(fa.uptime) : '—'],
              ['Memory', fa.memory_mb ? fmtBytes(fa.memory_mb * 1024 * 1024) : '—'],
              ['Last save', fa.save_age != null ? `${fmtUptime(fa.save_age)} ago` : '—'],
            ]}
            controls={<div className="tg-game-controls">{controlRow('factorio', fa)}</div>}/>
        )}
      </div>

      {dialog && <Confirm {...dialog} onClose={() => setDialog(null)}/>}
    </div>
  );
}
